import { App, Button, Form, Input, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { Link, useNavigate, useSearchParams } from "react-router";

import { QueryErrorAlert } from "design-system/components/QueryErrorAlert";
import { useSandboxAuth } from "sandbox/contexts/SandboxAuthContext";
import { sandboxPaths } from "sandbox/paths";

import s from "./ResetPasswordScreen.module.css";

interface FormValues {
  confirmPassword: string;
  newPassword: string;
}

interface ExpiredFormValues {
  email: string;
}

export const ResetPasswordScreen = () => {
  const { t } = useTranslation("sandbox");
  const { resetPassword, requestPasswordReset } = useSandboxAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [expiredSubmitting, setExpiredSubmitting] = useState(false);
  const [expiredError, setExpiredError] = useState("");
  const [expiredSent, setExpiredSent] = useState(false);
  const token = params.get("token");
  const name = params.get("name");
  const email = params.get("email");

  const onExpiredFinish = async (values: ExpiredFormValues) => {
    setExpiredSubmitting(true);
    setExpiredError("");
    try {
      await requestPasswordReset(values.email, "expired_link");
      setExpiredSent(true);
    } catch (e) {
      setExpiredError(
        (e as { message?: string })?.message ?? "An unexpected error occurred",
      );
    } finally {
      setExpiredSubmitting(false);
    }
  };

  if (!token) {
    if (expiredSent) {
      return (
        <>
          <Typography.Title level={2} className={s.heading}>
            {t("components.forgotPasswordScreen.checkEmail.title")}
          </Typography.Title>
          <Typography.Paragraph className={s.subtitle}>
            {t("components.forgotPasswordScreen.checkEmail.description", {
              email: email,
            })}
          </Typography.Paragraph>
        </>
      );
    }

    return (
      <>
        <Typography.Title level={2} className={s.heading}>
          {t("components.resetPasswordScreen.expired.title")}
        </Typography.Title>
        <Typography.Paragraph className={s.subtitle}>
          {t("components.resetPasswordScreen.expired.description")}
        </Typography.Paragraph>

        {expiredError && (
          <div className={s.errorAlert}>
            <QueryErrorAlert description={expiredError} />
          </div>
        )}

        <Form<ExpiredFormValues>
          layout="vertical"
          onFinish={onExpiredFinish}
          requiredMark={false}
          initialValues={{ email: email }}
          className={s.form}
        >
          <Form.Item
            name="email"
            label={t("labels.workEmail")}
            rules={[
              {
                required: true,
                message: t("validation.required", {
                  field: t("labels.workEmail"),
                }),
              },
              { type: "email", message: t("validation.emailType") },
            ]}
          >
            <Input size="large" placeholder={t("placeholders.workEmail")} />
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={expiredSubmitting}
            className={s.submit}
          >
            {expiredSubmitting
              ? t("actions.sendingLink")
              : t("components.resetPasswordScreen.expired.sendResetLink")}
          </Button>
        </Form>

        <Typography.Paragraph className={s.footer}>
          {t("auth.newToThredd")}{" "}
          <Link to={`${sandboxPaths.login}?mode=signup`}>
            {t("components.authScreen.createAccountLink")}
          </Link>
        </Typography.Paragraph>
      </>
    );
  }

  const onFinish = async (values: FormValues) => {
    setSubmitting(true);
    setError("");
    try {
      await resetPassword({ token, newPassword: values.newPassword });
      setDone(true);
      setTimeout(() => {
        navigate(sandboxPaths.login, { replace: true });
        message.success(t("components.resetPasswordScreen.success.message"));
      }, 1200);
    } catch (e) {
      setError(
        (e as { message?: string })?.message ?? "An unexpected error occurred",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <>
        <div className={s.iconBadgeSuccess} aria-hidden>
          <AiOutlineCheckCircle />
        </div>
        <Typography.Title level={2} className={s.heading}>
          {t("components.resetPasswordScreen.success.title")}
        </Typography.Title>
        <Typography.Paragraph className={s.subtitle}>
          {t("components.resetPasswordScreen.success.description")}
        </Typography.Paragraph>
      </>
    );
  }

  return (
    <>
      <Typography.Title level={2} className={s.heading}>
        {name
          ? t("components.resetPasswordScreen.greeting", { name })
          : t("components.resetPasswordScreen.greetingFallback")}
      </Typography.Title>
      <Typography.Paragraph className={s.subtitle}>
        {t("components.resetPasswordScreen.formSubtitle")}
      </Typography.Paragraph>

      {error && (
        <div className={s.errorAlert}>
          <QueryErrorAlert description={error} />
        </div>
      )}

      <Form<FormValues>
        layout="vertical"
        onFinish={onFinish}
        requiredMark={false}
        className={s.form}
      >
        <Form.Item
          name="newPassword"
          label={t("labels.password")}
          tooltip={t("validation.passwordExtra")}
          rules={[
            {
              required: true,
              message: t("validation.required", {
                field: t("labels.newPassword"),
              }),
            },
            { min: 8, message: t("validation.minChars", { count: 8 }) },
            {
              pattern: /\d/,
              message: t("validation.mustIncludeNumber"),
            },
          ]}
        >
          <Input.Password
            size="large"
            placeholder={t("placeholders.password")}
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label={t("labels.repeatPassword")}
          tooltip={t("components.updatePasswordModal.repeatPasswordTooltip")}
          dependencies={["newPassword"]}
          rules={[
            { required: true, message: t("validation.confirmNewPassword") },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("newPassword") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(t("validation.passwordsDontMatch")),
                );
              },
            }),
          ]}
        >
          <Input.Password
            size="large"
            placeholder={t("placeholders.password")}
          />
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          loading={submitting}
          className={s.submit}
        >
          {submitting
            ? t("actions.updating")
            : t("components.resetPasswordScreen.savePasswordAndLaunch")}
        </Button>
      </Form>

      <Typography.Paragraph className={s.footer}>
        {t("auth.alreadyHaveAccount")}{" "}
        <Link to={sandboxPaths.login}>{t("actions.signIn")}</Link>
      </Typography.Paragraph>
    </>
  );
};
